import cn from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import useTranslation from 'next-translate/useTranslation'
import React, { useState } from 'react'

import ButtonCircleIcon from '@/components/ButtonCircleIcon'
import Icon from '@/components/Icon'
import Gallery from '@/components/Icon/Gallery'
import Planimetry from '@/components/Icon/Planimetry'
import { ModalGallery } from '@/components/Modal/Types/Gallery'
import { RelatedAdsBanner } from '@/containers/ListingDetail/RelatedAdsBanner'
import { relatedAdsBannerOnDesktopGallery } from '@/containers/ListingDetail/RelatedAdsBanner/style.css'
import { ContextValue } from '@/context/Cities'
import { useDevice } from '@/context/Device'
import { ModalContextType } from '@/context/ModalContext'
import { useRelatedAdsBanner } from '@/context/RelatedAdsBannerProvider'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { ListingCate } from '@/page-commons/listingBySlug'

import ModalHeaderTitle from '../../ModalHeaderTitle'
import { modalIconText } from '../../style.css'
import { ModalTabs, TabContent } from '../ModalTabs'
import { ModalPlanimetry } from '../Planimetry'
import { detailModalHeader } from '../styles.css'

import { modalGalleryAndPlanimetry } from './index.css'

export type Content = 'gallery' | 'planimetry'

type Props = {
  cities: ContextValue
  citySlug: string
  closeModal: ModalContextType['closeModal']
  images: ListingCate['photos']
  openType: Content
  planimetryUrl: string
  startIndex: number
  title: string
}

export const ModalGalleryAndPlanimetry: React.FC<Props> = ({
  cities,
  citySlug,
  closeModal,
  images,
  openType,
  planimetryUrl,
  startIndex,
  title,
}) => {
  const { t } = useTranslation()
  const [currentContent, setCurrentContent] = useState<Content>(openType)
  const tabClickHandler = (content) => setCurrentContent(content)
  const [currentIndex, setCurrentIndex] = useState(startIndex)
  const listingCity = cities.list.find((item) => item.slug === citySlug)
  const device = useDevice()
  const modalType = 'show-planimetry'
  const { showRelatedAdsBanner } = useRelatedAdsBanner()

  const tabPhotos: TabContent = {
    eventLabel: 'PhotosChip',
    icon: <Gallery />,
    key: 'gallery',
    label: t('common:photos'),
  }
  const tabPlanimetry: TabContent = {
    eventLabel: 'PlanimetryChip',
    icon: <Planimetry />,
    key: 'planimetry',
    label: t('listing-detail:planimetry'),
  }

  // for listings in France, planimetry is just a placeho
  const showPlanimetry = citySlug !== 'fr/paris'

  const tabsContent: Array<TabContent> = showPlanimetry ? [tabPhotos, tabPlanimetry] : [tabPhotos]

  const tabs = planimetryUrl ? (
    <ModalTabs activeTab={currentContent} tabs={tabsContent} onTabClick={tabClickHandler} />
  ) : (
    <>
      <div className={modalIconText}>{t('common:photos')}</div>
    </>
  )

  return (
    <div className={modalGalleryAndPlanimetry}>
      <div>
        <div className={detailModalHeader}>
          {device === 'desktop' ? (
            <>
              <ModalHeaderTitle cityName={listingCity.translatedCityName} closeModal={closeModal} title={title} />
              {tabs}
            </>
          ) : (
            <ButtonCircleIcon ariaLabel="close dialog" onClick={() => closeModal()}>
              <Icon.Close />
            </ButtonCircleIcon>
          )}
        </div>
        {device === 'mobile' && planimetryUrl && (
          <ModalTabs
            activeTab={currentContent}
            className={sprinkles({ paddingLeft: 'm' })}
            tabs={tabsContent}
            onTabClick={tabClickHandler}
          />
        )}
      </div>
      {currentContent === 'gallery' ? (
        <motion.div
          animate={{ opacity: 1 }}
          className={sprinkles({ margin: 'auto', width: '100%' })}
          initial={{ opacity: 0 }}
        >
          <ModalGallery images={images} startIndex={currentIndex} onIndexChange={setCurrentIndex} />
        </motion.div>
      ) : (
        <ModalPlanimetry url={planimetryUrl} />
      )}

      <AnimatePresence>
        {showRelatedAdsBanner && (
          <RelatedAdsBanner
            className={cn(
              sprinkles({ marginTop: { mobile: 'auto' }, marginX: 'm' }),
              device === 'desktop' && relatedAdsBannerOnDesktopGallery
            )}
            touchPoint="FromPopup"
            onClickCallback={closeModal}
          />
        )}
      </AnimatePresence>
    </div>
  )
}
